import React, { Fragment, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearStripePaymentInfos, clearUserPaymentInfos, getSubscriptionPlansRequest, selectSubscriptionPlan } from 'redux/actions';
import {
  getFilteredSubscriptionPlans,
  hasSubscription,
  hasZppCourseAttendances,
  isCouponNotFound,
  isSubscriptionPlansLoading
} from 'redux/query';
import qs from 'query-string';
import { SUBSCRIPTION_PLAN_GROUPS } from 'utils/subscriptionPlanGroups';
import Style from './PurchasePage.style';
import { Col, Container, Row, Spinner } from 'Common';
import { FlashListContainer } from 'containers';
import { TEXT } from 'locales/translations';
import SubscriptionCard from '../../Plans/SubscriptionCard';
import { CheckoutCouponForm, PreventionPromoCard } from 'Component';
import { useHistory, useLocation } from 'react-router-dom';
import { linkHandler } from 'utils/linkHandler';
import { NewYearsBanner } from 'components/Component';

export const PurchasePage = () => {
  const location = useLocation();
  const history = useHistory();

  const couponNotFound = useSelector(isCouponNotFound);
  const coupon = useMemo(() => {
    let { coupon } = qs.parse(location.search);
    if (couponNotFound) {
      return null;
    }
    return coupon;
  }, [location.search, couponNotFound]);

  const dispatch = useDispatch();
  const isSubscribed = useSelector(hasSubscription);
  const hasAttendances = useSelector(hasZppCourseAttendances);
  const refererToUserPage = isSubscribed || hasAttendances;

  const isLoading = useSelector(isSubscriptionPlansLoading);
  const subscriptionPlans = useSelector(getFilteredSubscriptionPlans(coupon));

  const priority = { MONTHLY: 0, YEARLY: 1, ONETIME: 2 };

  const sortedPlans = subscriptionPlans.standard
    .slice()
    .sort((a, b) => (priority[a.backendIntervalNaming] ?? 99) - (priority[b.backendIntervalNaming] ?? 99));

  const header = !!coupon ? TEXT.subscriptionPlans.reducedHeading : TEXT.subscriptionPlans.finalHeading;
  const subheader = !!coupon ? TEXT.plans.reducedSubheading : TEXT.plans.subheading;

  useEffect(() => {
    if (refererToUserPage) {
      history.replace(linkHandler.get('profilePersonalData'));
      return;
    }
    dispatch(clearUserPaymentInfos());
    dispatch(clearStripePaymentInfos());
    dispatch(getSubscriptionPlansRequest(SUBSCRIPTION_PLAN_GROUPS.NULL, coupon));
  }, [refererToUserPage]);

  const handleOnSelectPlan = (plan) => () => {
    if (plan.zppCertified) {
      history.push(linkHandler.get('sda') + '#section-checkout');
      return;
    }

    dispatch(selectSubscriptionPlan(plan.id));
    history.push(linkHandler.get('checkout'));
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <NewYearsBanner />
      <Style backgroundColor="white">
        <Container>
          <Row>
            <Col xs={{ size: 12 }} lg={{ size: 10, offset: 1 }} className="mt-4 mt-sm-0">
              <h1 className="plans__heading" dangerouslySetInnerHTML={{ __html: header }}></h1>
              <p className="plans__subheading">{subheader}</p>
              <FlashListContainer />
            </Col>
          </Row>
        </Container>
        {sortedPlans || subscriptionPlans.badged ? (
          <Fragment>
            <Row>
              <Col lg={{ size: 10, offset: 1 }}>
                <Row>
                  <Col className="subscriptions" xs="12" md="6">
                    {!coupon && <PreventionPromoCard />}
                    {subscriptionPlans.badged
                      .filter((plan) => !coupon || !plan.zppCertified)
                      .map((plan) => (
                        <SubscriptionCard key={plan.id} plan={plan} onClick={handleOnSelectPlan(plan)} hasCoupon={!!coupon} />
                      ))}
                  </Col>
                  <Col className="subscriptions spac" xs="12" md="6">
                    {sortedPlans.map((plan) => (
                      <SubscriptionCard key={plan.id} plan={plan} onClick={handleOnSelectPlan(plan)} hasCoupon={!!coupon} />
                    ))}
                  </Col>
                </Row>
                <Col className="mt-4">
                  <CheckoutCouponForm />
                </Col>
              </Col>
            </Row>
            <Container>
              <Row>
                <Col xs={{ size: 12, offset: 0 }} md={{ size: 9, offset: 1 }}>
                  <p className="small text-center subscription-disclaimer">{TEXT.checkout.subscriptionDisclaimer}</p>
                </Col>
              </Row>
            </Container>
          </Fragment>
        ) : (
          <p className="text-center">{TEXT.checkout.noAbos}</p>
        )}
      </Style>
    </>
  );
};

export default PurchasePage;
